// Re-taking the GPS fix between "Save & another" observations (scout-03).
//
// The capture form takes ONE fix when it opens — deliberately, because a
// getCurrentPosition inside the save can hang the whole save on iOS (see the
// gpsPos comment in Scouting.tsx). "Save & another" then keeps the modal
// mounted and keeps grower/block/scout/date, so the open-time effect never
// re-fires and every later find was stamped with the fix from wherever the
// scout opened the form. Ten finds down a 500 m row landed as ten pins on one
// spot — and the edit path strips lat/lng/gps_accuracy_m, so the pin could
// never be corrected afterwards.
//
// The fix is to re-take AFTER the save has landed (never inside it, so the
// hang risk stays where it was) — which is what this owns, along with the
// ordering that makes it safe.

import { useCallback, useEffect, useRef, useState } from 'react'
import { pointInBoundary, type BlockBoundary } from '@/lib/blockBoundary'
import { getCurrentPositionOnce, type ScoutPoint } from '@/hooks/useGeolocation'

export interface GpsFix {
  lat:        number
  lng:        number
  accuracy_m: number
}

/**
 * Take a fresh fix for the NEXT observation.
 *
 * Clears first and awaits second, so between the two the form holds NO
 * coordinates: a re-take that is slow, denied or times out can never leave the
 * previous find's coordinates in place to be stamped on this one. An
 * observation with no pin is a gap on the map; an observation with the
 * previous find's pin is false evidence, and false evidence is worse — it is
 * the map a manager plans a spray from.
 *
 * `read` is injected so the caller passes getCurrentPositionOnce (and a test
 * passes a reader it can move).
 */
export async function retakeFix(
  setFix: (f: GpsFix | null) => void,
  read: () => Promise<GpsFix | null>,
): Promise<GpsFix | null> {
  setFix(null)
  let pos: GpsFix | null = null
  try {
    pos = await read()
  } catch {
    pos = null   // best-effort, exactly like the open-time capture
  }
  const fix = pos ? { lat: pos.lat, lng: pos.lng, accuracy_m: pos.accuracy_m } : null
  setFix(fix)
  return fix
}

/**
 * Which fix an observation is stamped with.
 *
 * While a WALK is recording, the walk's own watchPosition is already producing
 * a live, accuracy-gated fix — so the position at the moment of saving is
 * available for free, with none of the hang risk of calling geolocation inside
 * the save. That is the right answer whenever it exists, and it is exactly the
 * case the defect describes: a scout walking a row, logging find after find.
 *
 * With no walk running there is no live feed, and the best available is the
 * last one taken — at open, or by the re-take after the previous "Save &
 * another". Closer than the modal-open fix, still not the save-moment truth,
 * which is why the walk fix wins when there is one.
 */
export function fixForObservation(
  liveWalkFix: WalkFix | null | undefined,
  lastTaken: GpsFix | null,
  nowMs: () => number = () => Date.now(),
): GpsFix | null {
  // F038: a walk's "live" fix is only live while the GPS is feeding it. A
  // walk resumed from a draft, or one whose GPS stopped under a shed roof,
  // carries a last point that may be an hour old — and this stamped it on
  // every find as if the scout were still standing there. A live fix older
  // than WALK_FIX_MAX_AGE_MS is not used; the last fix taken at open (or by
  // the re-take) stands in, and with neither the pin is NULL, never a guess.
  if (liveWalkFix && walkFixIsFresh(liveWalkFix, nowMs)) return { lat: liveWalkFix.lat, lng: liveWalkFix.lng, accuracy_m: liveWalkFix.acc }
  return lastTaken
}

/** A walk fix with no timestamp is taken as fresh (legacy shape); one with a
 *  timestamp is fresh for WALK_FIX_MAX_AGE_MS. */
function walkFixIsFresh(fix: WalkFix, nowMs: () => number = () => Date.now()): boolean {
  return fix.t == null || nowMs() - fix.t <= WALK_FIX_MAX_AGE_MS
}

export type WalkFix = Pick<ScoutPoint, 'lat' | 'lng' | 'acc'> & { t?: number }

/** A walk fix older than this is a resumed draft's last point, not where the
 *  scout is: it must not pick a block. */
export const WALK_FIX_MAX_AGE_MS = 10 * 60_000

export type BlockHit =
  | { kind: 'one'; blockId: string }
  | { kind: 'none' }
  | { kind: 'many' }

/**
 * Which drawn block contains this fix — the ONE rule every auto-pick uses.
 * Exactly one containing polygon names the block; none (a road, a gate) or
 * several (overlapping drawings) is not an answer, and is reported as such so
 * the caller can say why rather than guess. Stops at the second hit.
 */
export function blockContaining(
  fix: { lat: number; lng: number },
  blocks: ReadonlyArray<{ block_id: string; boundary: BlockBoundary | null }>,
): BlockHit {
  let found: string | null = null
  for (const b of blocks) {
    if (!b.boundary || !pointInBoundary(fix.lng, fix.lat, b.boundary)) continue
    if (found !== null) return { kind: 'many' }
    found = b.block_id
  }
  return found === null ? { kind: 'none' } : { kind: 'one', blockId: found }
}

/**
 * The block a walk was mostly IN — the modal block over the track's points,
 * so Stop can stamp scout_walks.block_id and the Walks filter, the cards and
 * the GlobalGAP report attribute the walk to a block. Null when the track sits
 * outside every drawn boundary (WalkEditModal's picker fills those in).
 *
 * F120: this lived in Scouting.tsx on its own fork of the point-in-polygon
 * test, first-match-wins over overlapping drawings, while every other
 * auto-pick used blockContaining. One rule now: a point inside exactly one
 * block counts for it; a point inside several (overlapping drawings) is not
 * evidence for any and is skipped, the same way a single fix over two blocks
 * is "many", not a guess.
 */
export function dominantBlockFromTrack(
  points: ReadonlyArray<{ lat: number; lng: number }>,
  blocks: ReadonlyArray<{ block_id: string; boundary: BlockBoundary | null }>,
): string | null {
  const counts = new Map<string, number>()
  for (const p of points) {
    const hit = blockContaining(p, blocks)
    if (hit.kind === 'one') counts.set(hit.blockId, (counts.get(hit.blockId) ?? 0) + 1)
  }
  let best: string | null = null
  let max = 0
  counts.forEach((n, id) => { if (n > max) { max = n; best = id } })
  return best
}

/**
 * Where a held pick moves on a new hit: into the one block that contains the
 * fix, or nowhere — a fix on the road between blocks, or in overlapping
 * drawings, is a gap in the map, not evidence the find is nowhere. Only the
 * first pick may come from nothing.
 */
export function followBlock(current: string, hit: BlockHit): string {
  return hit.kind === 'one' ? hit.blockId : current
}

export interface WalkBlock {
  /** The held pick — the last block a fix was inside; '' until one is found. */
  blockId: string
  /** What the LATEST fix said, so a surface can say "holding" on a road. */
  hit:     BlockHit
}

/**
 * The block the walk is in right now, derived ONCE at the page and handed to
 * every capture surface — so Quick log and the full form can never disagree
 * about where the scout is. Held across road / overlap fixes; null with no
 * walk recording, or when the walk's latest point is a stale seed.
 */
export function useWalkBlock(
  liveWalkFix: WalkFix | null | undefined,
  blocks: ReadonlyArray<{ block_id: string; boundary: BlockBoundary | null }>,
  nowMs: () => number = () => Date.now(),
): WalkBlock | null {
  const [state, setState] = useState<WalkBlock | null>(null)
  const fresh = liveWalkFix != null && walkFixIsFresh(liveWalkFix, nowMs)
  const lat = liveWalkFix?.lat, lng = liveWalkFix?.lng
  useEffect(() => {
    if (!fresh || lat == null || lng == null) { setState(null); return }
    const hit = blockContaining({ lat, lng }, blocks)
    // Bail out when nothing the consumers read has changed (F116): a fix
    // every second inside the same block used to mint a fresh object each
    // time, and every reader of walkBlock re-rendered with it.
    setState(prev => {
      const next: WalkBlock = { blockId: followBlock(prev?.blockId ?? '', hit), hit }
      return prev && sameWalkBlock(prev, next) ? prev : next
    })
  }, [fresh, lat, lng, blocks])
  return state
}

/** Same held block and same kind of hit (and the same hit block, when one). */
function sameWalkBlock(a: WalkBlock, b: WalkBlock): boolean {
  if (a.blockId !== b.blockId || a.hit.kind !== b.hit.kind) return false
  return a.hit.kind !== 'one' || b.hit.kind !== 'one' || a.hit.blockId === b.hit.blockId
}

/**
 * Quick log's position + the block it implies.
 *
 * With a walk recording, the walk's block IS the pick and its live fix IS the
 * position (no one-shot read, no 30 s maximumAge cache handing back where the
 * scout stood a block ago); the pick follows the walk until the scout picks a
 * block by hand. Picking the blank option hands the choice back to GPS. With
 * no walk, one fix is read at open and the pick is derived from it by the
 * same rule.
 */
/** The recording walk as a capture surface sees it — null when none records. */
export interface WalkView {
  id:    string | null
  block: WalkBlock | null
  fix:   WalkFix | null
}

export const walkIdOf = (w: WalkView | null): string | null => w?.id ?? null

export function useQuickLogPosition(
  blocks: ReadonlyArray<{ block_id: string; boundary: BlockBoundary | null }>,
  walk: WalkView | null,
) {
  const walkBlock = walk?.block ?? null
  const liveWalkFix = walk?.fix ?? null
  const [oneShot, setOneShot] = useState<GpsFix | null>(null)
  const [blockId, setBlockIdState] = useState('')
  const [pickMode, setPickMode] = useState<'auto' | 'manual'>('auto')
  // Read once, at open, only when no walk is feeding a FRESH live fix — never
  // again for the life of the modal (a re-read is where a cached fix would
  // come from). A stale walk fix (F038) counts as no fix: take the one-shot.
  const liveAtOpen = useRef(liveWalkFix != null && walkFixIsFresh(liveWalkFix))
  useEffect(() => {
    if (liveAtOpen.current) return
    let cancelled = false
    getCurrentPositionOnce(8_000).then(p => {
      if (!cancelled && p) setOneShot({ lat: p.lat, lng: p.lng, accuracy_m: p.accuracy_m })
    }).catch(() => {})
    return () => { cancelled = true }
  }, [])

  const pos = fixForObservation(liveWalkFix, oneShot)
  const walkPick = walkBlock?.blockId ?? null
  useEffect(() => {
    if (pickMode === 'manual') return
    if (walkPick != null) { setBlockIdState(walkPick); return }
    if (oneShot) setBlockIdState(prev => followBlock(prev, blockContaining(oneShot, blocks)))
  }, [pickMode, walkPick, oneShot, blocks])

  const setBlockId = (id: string) => {
    if (id === '') { setPickMode('auto'); setBlockIdState(''); return }
    setPickMode('manual'); setBlockIdState(id)
  }

  // "Save & next" (FM02 UF-01): a fresh fix for the next find, read AFTER the
  // save landed — never inside it. The block pick follows the new fix by the
  // rule above (a hand pick stays put). With a walk feeding a fresh live fix
  // there is nothing to read: the walk IS the position. `retaking` is what the
  // form says while it reads, and 'failed' when no fix came — the next find
  // then saves with no pin, never the previous find's (retakeFix).
  const [retaking, setRetaking] = useState<'locating' | 'failed' | null>(null)
  const walkIsLive = liveWalkFix != null && walkFixIsFresh(liveWalkFix)
  const retake = useCallback(async () => {
    if (walkIsLive) return
    setRetaking('locating')
    const next = await retakeFix(setOneShot, () => getCurrentPositionOnce(8_000))
    setRetaking(next ? null : 'failed')
  }, [walkIsLive])
  return { pos, blockId, setBlockId, pickMode, retake, retaking }
}

export type AutoPickStatus = 'matched' | 'holding' | 'outside-blocks' | 'no-polygons'

/**
 * The full form's auto-pick decision, pure so it is tested rather than read off
 * an effect. Source of truth: the page's held walk block while a walk records
 * (Quick log reads the same one, so the two cannot disagree), else the fix
 * taken at open / re-taken after "Save & another", through the same
 * containment rule. Returns the block to move to (null = stay) and what the
 * banner should say; null status = nothing to say yet (no fix).
 */
export function resolveAutoPick(args: {
  current:   string
  walkBlock: WalkBlock | null | undefined
  fix:       { lat: number; lng: number } | null
  blocks:    ReadonlyArray<{ block_id: string; boundary: BlockBoundary | null }>
}): { moveTo: string | null; status: AutoPickStatus | null } {
  const { current, walkBlock, fix, blocks } = args
  if (!blocks.some(b => b.boundary)) return { moveTo: null, status: 'no-polygons' }
  const hit = walkBlock ? walkBlock.hit : fix ? blockContaining(fix, blocks) : null
  if (!hit) return { moveTo: null, status: null }
  const next = walkBlock ? walkBlock.blockId : followBlock(current, hit)
  if (next && next !== current) return { moveTo: next, status: 'matched' }
  if (!next) return { moveTo: null, status: 'outside-blocks' }
  return { moveTo: null, status: hit.kind === 'one' ? 'matched' : 'holding' }
}
